/**
 * ItemPoolEditor — the pools on the left; the chosen pool's ranked members on
 * the right.
 *
 * The members are drawn by the item list's own block (ItemQueryWorkspace,
 * roadmap N-16): the same query bar, filters and columns as the items list,
 * in the pool's rank order, with the rank, drag and the cut line. Filtering a
 * ranked pool is a view only (owner ruling, 2026-10-14): a notice says how
 * many members show and that the cut line and order are unchanged; each row
 * keeps its rank in the whole pool; and while a filter is on nothing can be
 * dragged and the cut line does not move.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { v5 as uuidv5 } from 'uuid';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { arrayMove } from '@dnd-kit/sortable';
import { ListChecks, Plus, CheckCircle, Clock, Archive, Download, ChevronDown, ChevronRight, Plug, Sparkles, X, AlertTriangle, GitMerge, ShieldCheck, ListFilter, FolderOpen, Loader2, Search, Landmark } from 'lucide-react';
import { suggestClusters } from '../../../lib/llmClusteringService';
import { useAiCapability } from '../../../lib/ai/useAiCapability';
import type { SuggestClustersResult } from '../../../lib/llmClusteringService';
import { PageHeader } from '../PageHeader';
import { CreateItemPoolModal } from '../CreateItemPoolModal';
import { PoolScopeAssignBanner } from './PoolScopeAssignBanner';
import { useItemPoolsStore } from '../../../stores/prioritization/itemPoolsStore';
import { useTrackStore } from '../../../stores/prioritization/trackStore';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { saveImportRows, type ImportSaveOutcome } from '../../../lib/importBatch';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useCriteriaStore } from '../../../stores/prioritization/criteriaStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { usePermissions } from '../../../hooks/prioritization/usePermissions';
import { usePoolPriorities } from '../../../hooks/prioritization/usePoolPriorities';
import type { ShapedItem } from '../../../types/prioritization/lifecycle';
import { dedupeById } from '../../../lib/dedupeById';
import { isClosedDuplicate } from '../../../lib/clusterResolution';
import { getItemCountState } from '../../collection/types';
import { ReadinessBadge } from '../item-list/components/ReadinessBadge';
import type { Priority } from '../../../types/prioritization';
import { RankMark } from '@/components/prioritization/RankMark';
import { CutLineControl } from './CutLineControl';
import { cutLineSlot, cutLineSlotAmong } from './cutLineSlot';
import { indicatorTopForSlot, slotForPointer } from './cutLineDrag';
import { IntegrationSyncPanel } from './IntegrationSyncPanel';
import { canUserPublish, checkPoolPublishReadiness, publishItemPool, archiveItemPool, type PublishStatus } from '../../../lib/publishService';
import { generateCSV } from '../../../lib/connectors';
import { PublicationStatusBadge, PostPublishChangeAlert } from '../validation';
import { ReverseActionButton } from '../../reversals/ReverseActionButton';
import { republishItemPool } from '../../../lib/reversals/reversalsService';
import { RationaleFields } from '../decisions/RationaleFields';
import { EMPTY_RATIONALE, toRationaleParts, type RationaleValue, type RationaleParts } from '../../../lib/rationaleParts';
import { useIsAdmin } from '../../../hooks/prioritization/useScopedPermissions';
import { canImportPoolRanks } from '../../../lib/poolAuthority';
import { UI_LABELS } from '../../../lib/terminology';
import { getBlockingReadinessIssues } from '../../../lib/itemReadiness';
import { CHIP_OVERFLOW_CLASS, CLASSIFICATION_CHIP_CLASS } from '../../../lib/classificationDisplay';
import { approvePriorityList } from '../../../lib/commands/approvePriorityList';
import { usePriorityListApproveRight } from '../../../hooks/prioritization/useActRight';
import { refusedByRight } from '../../../lib/actRight';
import { poolQueryLink } from '../../../lib/poolQueryLink';
import { AddFromCollectionModal } from '../item-list/components/AddFromCollectionModal';
import { PoolFeedPanel } from './PoolFeedPanel';
import { useQueryFedPoolsEnabled } from '../../../features/pools/hooks/useQueryFedPoolsEnabled';
import { DecisionTrailPanel } from '../DecisionTrailPanel';
import { useDecisionTrailParam } from '../../../hooks/prioritization/useDecisionTrailParam';
import { useSessionBehindPool } from '../../../hooks/prioritization/useSessionBehindPool';
import { StructureVersionFinePrint } from '@/components/hierarchy/StructureVersionFinePrint';
import { useUserNameMap } from '../../../hooks/useUserNameMap';
import { useItemQueryWorkspace } from '../item-list/hooks/useItemQueryWorkspace';
import type { FilterGroup } from '../item-list/hooks/useItemListState';
import type { ItemRow } from '../item-list/hooks/useItemListData';
import {
  ItemQueryBar,
  ItemQueryColumnsButton,
  ItemQueryFiltersButton,
  ItemQueryRows,
  ItemQueryTable,
} from '../item-list/components/ItemQueryWorkspace';
import { useItemHref } from '@/lib/itemLinkScope';
import { useItemLinkScope } from '@/lib/itemLinkScope';
import { itemExitStatus } from '@/lib/intakeQueueVisibility';

/** A pool opens on every member: no filter. */
const NO_FILTERS: FilterGroup[] = [];
/**
 * A pool's columns are the person's own in this browser, apart from the item
 * list's. The default is what the pool's row showed before it moved onto the
 * item list's table (title and tags), plus category and altitude; any other
 * column is in Columns.
 */
const POOL_COLUMNS = {
  storageKey: 'itemPoolVisibleColumns.v1',
  defaults: ['title', 'itemCategory', 'altitude', 'tags'],
};

export function ItemPoolEditor() {
  const itemHref = useItemHref();
  const poolClusteringEnabled = useAiCapability('pool_clustering');
  const queryFedPoolsEnabled = useQueryFedPoolsEnabled();
  const [showAddItems, setShowAddItems] = useState(false);
  const location = useLocation();
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [clusterResult, setClusterResult] = useState<SuggestClustersResult | null>(null);
  const [isClusterLoading, setIsClusterLoading] = useState(false);
  const navigate = useNavigate();
  const [isPublishing, setIsPublishing] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  // Gated reorder of a published pool: the pending order awaiting a rationale.
  const [pendingReorder, setPendingReorder] = useState<string[] | null>(null);
  const [reorderRationale, setReorderRationale] = useState('');
  // Published pool: the cut-line move waiting for its rationale (canon 27).
  const [pendingCutLine, setPendingCutLine] = useState<number | null>(null);
  // The rest of the four-part rationale (985), optional: for that cut-line
  // move, and for publishing the list. Each rides on the Decision its act writes.
  const [cutLineRationale, setCutLineRationale] = useState<RationaleValue>(EMPTY_RATIONALE);
  const [publishRationale, setPublishRationale] = useState<RationaleValue>(EMPTY_RATIONALE);
  // Dragging the cut line: the list the rows live in, the position the line
  // would land on, and where to draw it while the pointer is down. The rows
  // never move during a drag; a second dashed line follows the pointer and
  // the saved line moves only when the server has accepted the new position.
  const poolListRef = useRef<HTMLDivElement>(null);
  const [cutDraft, setCutDraft] = useState<number | null>(null);
  const [cutDragTop, setCutDragTop] = useState<number | null>(null);
  // "Why is this #N?" — the Decision Trail inspector, URL-addressable via ?why=
  const { trailItemId, openTrail, closeTrail } = useDecisionTrailParam();
  
  const clerkUserId = useUserStore((state) => state.userId);
  const itemPools = useItemPoolsStore((state) => state.itemPools);
  // Where each pool sits and whether a plan took it — what the old /priorities overview showed.
  const tracks = useTrackStore((state) => state.tracks);
  const cycles = useDegapProcessStore((state) => state.cycles);
  const trackNameById = useMemo(() => new Map(tracks.map((t) => [t.id, t.name])), [tracks]);
  const cycleNameById = useMemo(() => new Map(cycles.map((c) => [c.id, c.name])), [cycles]);
  const activeItemPoolId = useItemPoolsStore((state) => state.activeItemPoolId);
  const setActiveItemPool = useItemPoolsStore((state) => state.setActiveItemPool);
  // "Delete list" is a governed retire now (mig 758 retire_item_pool): reason
  // required, refused once the list is locked/published/used, reversible.
  const retireItemPool = useItemPoolsStore((state) => state.retireItemPool);
  const restoreRetiredItemPool = useItemPoolsStore((state) => state.restoreRetiredItemPool);
  const retiredItemPools = useItemPoolsStore((state) => state.retiredItemPools);
  const updateItemPool = useItemPoolsStore((state) => state.updateItemPool);
  const updateRanking = useItemPoolsStore((state) => state.updateRanking);
  const updateCutLine = useItemPoolsStore((state) => state.updateCutLine);
  const addPriorityToList = useItemPoolsStore((state) => state.addPriorityToList);
  const removePriorityFromList = useItemPoolsStore((state) => state.removePriorityFromList);
  const publishItemPoolStore = useItemPoolsStore((state) => state.publishItemPool);
  const handOffToPlanning = useItemPoolsStore((state) => state.handOffToPlanning);
  const acknowledgePostPublishChange = useItemPoolsStore((state) => state.acknowledgePostPublishChange);
  
  const getItemById = useItemsStore((state) => state.getItemById);
  // F11: `getItemById` is a stable function reference, so memoizing the ranked
  // list on it alone froze the result computed *before* the items store
  // resolved — pools load first, every lookup missed, and the pool rendered
  // empty until the user switched pools and back. Depend on the items array
  // itself so the list recomputes the moment items land.
  const itemsData = useItemsStore((state) => state.items);
  const promoteToPriority = useItemsStore((state) => state.promoteToPriority);
  const updateItem = useItemsStore((state) => state.updateItem);
  const createItemWithReason = useItemsStore((state) => state.createItemWithReason);
  const attributes = useAttributesStore((state) => state.attributes);
  const requiredAttributes = useMemo(() => attributes.filter(attr => attr.required), [attributes]);
  
  const addNotification = useUIStore((state) => state.addNotification);
  const userId = useUserStore((state) => state.userId);
  const selectedCriteriaIds = useCriteriaStore((state) => state.selectedCriteriaIds);
  const permissions = usePermissions();
  const isWorkspaceAdmin = useIsAdmin();
  const fetchItemPools = useItemPoolsStore((state) => state.fetchFromSupabase);
  const canCreatePool = permissions.canCreateFocusLists;
  const [createOpen, setCreateOpen] = useState(false);
  const handlePoolCreated = useCallback((id: string) => {
    setCreateOpen(false);
    setActiveItemPool(id);
  }, [setActiveItemPool]);
  
  // Select first list if none selected (ref prevents update loop when store syncs)
  const hasAutoSelectedRef = useRef(false);
  useEffect(() => {
    if (itemPools.length === 0) {
      hasAutoSelectedRef.current = false;
      return;
    }
    if (activeItemPoolId) return;
    if (hasAutoSelectedRef.current) return;
    hasAutoSelectedRef.current = true;
    setActiveItemPool(itemPools[0].id);
  }, [activeItemPoolId, itemPools, setActiveItemPool]);

  // /prioritization/lists/:listId opens that pool once it has loaded. Applied
  // once per listId, so picking another pool on this page still works; an id
  // that is not loaded leaves the choice above alone. Must run after the
  // auto-select effect so the route's pool wins on first load.
  const { listId: routeListId } = useParams<{ listId?: string }>();
  const appliedRouteListIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!routeListId || appliedRouteListIdRef.current === routeListId) return;
    if (!itemPools.some((pool) => pool.id === routeListId)) return;
    appliedRouteListIdRef.current = routeListId;
    setActiveItemPool(routeListId);
  }, [routeListId, itemPools, setActiveItemPool]);

  const activeList = itemPools.find(list => list.id === activeItemPoolId);
  useItemLinkScope({ cycleId: activeList?.cycleId });
  // 1017: the database's answer, as approve_priority_list will judge it. It
  // decides whether Ratify is drawn, so the page and the act cannot disagree.
  const approveRight = usePriorityListApproveRight(activeList?.cycleId ?? null, activeList?.workspaceId ?? null);
  // S-20: a pool a session produced takes its cut line from that session.
  const sessionBehindPool = useSessionBehindPool(activeList?.id);
  
  // Get priorities for active list (contextual: list membership + rank)
  // F11: derived from the items *data* (not the store's stable function refs),
  // and it tells "empty pool" apart from "items still loading".
  const { priorities, isPending: prioritiesPending } = usePoolPriorities(activeList);

  // The members, drawn by the item list's block in rank order. A query here
  // narrows the view only; the pool's order and cut line stay as they are.
  const ws = useItemQueryWorkspace({
    items: priorities,
    syncUrl: false,
    initialFilters: NO_FILTERS,
    columns: POOL_COLUMNS,
    ranked: true,
  });
  const poolFiltered = ws.queryActive;
  const { clearQuery } = ws;
  // Another pool opens on all of its members.
  useEffect(() => {
    clearQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when the pool changes
  }, [activeList?.id]);
  // Each member's place in the whole pool — what its rank says while a filter shows only some.
  const poolIndexById = useMemo(() => new Map(priorities.map((p, i) => [p.id, i])), [priorities]);
  const memberNames = useUserNameMap(priorities.flatMap((p) => [p.ownerId, p.createdBy]));
  
  // Get available items: any item not already in this list (ready or not).
  // F14: dedupe by id — optimistic creates and a subsequent sync can leave the
  // same item in the store twice, and the panel rendered it twice.
  const allItems = useMemo(
    // Same predicate as itemsStore.getItems(), applied to the items array so
    // the memo tracks the data rather than a stable function reference.
    // A resolved duplicate (Doc 12 Rule 1) points at its canonical and is not a
    // prioritization object any more — only the canonical is offered.
    () => dedupeById(itemsData.filter((c) => c.lifecycle_state === 'item' && !isClosedDuplicate(c as unknown as Parameters<typeof isClosedDuplicate>[0])) as ShapedItem[]),
    [itemsData],
  );
  const poolMemberIds = useMemo(
    () => new Set(activeList?.priorityIds ?? []),
    [activeList],
  );
  // A retired item (exit_status 'archived') is out of play and is never offered.
  const availableItems = useMemo(
    () => allItems.filter((item) => !poolMemberIds.has(item.id) && itemExitStatus(item) !== 'archived'),
    [allItems, poolMemberIds],
  );

  // F14: zero/one/few/many — at `many` the panel gets a search input.
  const [addItemsQuery, setAddItemsQuery] = useState('');
  const availableCountState = getItemCountState(availableItems.length);
  const filteredAvailableItems = useMemo(() => {
    const q = addItemsQuery.trim().toLowerCase();
    if (!q) return availableItems;
    return availableItems.filter((item) => item.title.toLowerCase().includes(q));
  }, [availableItems, addItemsQuery]);

  // Readiness for ranking is enforced when the pool locks (TrackDetail,
  // evaluateReadyForRanking). Joining a pool is held back only by an every-item
  // Hard Stop (itemPoolsStore.addPriorityToList); by default only the title is
  // one, so items missing anything else are addable and counted here.
  const unreadyAvailableCount = useMemo(
    () =>
      filteredAvailableItems.filter(
        (item) => getBlockingReadinessIssues(item, requiredAttributes).length > 0,
      ).length,
    [filteredAvailableItems, requiredAttributes],
  );
  
  const applyAboveTheLine = (orderedPriorities: Priority[]) => {
    if (!activeList) return;
    orderedPriorities.forEach((priority, index) => {
      const newAboveTheLine = index < activeList.cutLinePosition;
      if (priority.aboveTheLine !== newAboveTheLine) {
        updateItem(priority.id, { ...priority, rank: index + 1, aboveTheLine: newAboveTheLine });
      }
    });
  };

  // A row dragged onto another. Never while a filter is on: the rows on
  // screen are not the whole order (owner ruling, 2026-10-14).
  const handleReorder = (activeId: string, overId: string) => {
    if (!activeList || !permissions.canRankPriorities || poolFiltered) return;

    const oldIndex = priorities.findIndex(p => p.id === activeId);
    const newIndex = priorities.findIndex(p => p.id === overId);

    if (oldIndex !== newIndex) {
      applyOrder(arrayMove(priorities, oldIndex, newIndex).map(p => p.id));
    }
  };

  // The one reorder path: the drag handle and a re-imported file's ranks.
  const applyOrder = (newPriorityIds: string[], message = 'Priority order has been updated') => {
    if (!activeList) return;
    const result = updateRanking(activeList.id, newPriorityIds);

    // Published pool: the reorder is gated — collect a rationale first
    // (canon 27: every reorder is memorialized — who, when, why).
    if (result.requiresRationale) {
      setPendingReorder(newPriorityIds);
      return;
    }

    applyAboveTheLine(
      newPriorityIds
        .map((id) => priorities.find((p) => p.id === id))
        .filter((p): p is Priority => !!p),
    );

    addNotification({
      type: 'info',
      title: 'Ranking Updated',
      message,
      duration: 2000
    });
  };

  // May a re-imported file change this pool's order? In a cycle (the pool's
  // own, or its track's) only the cycle owner; outside one, whoever may
  // reorder it (owner ruling 2026-11-02, lib/poolAuthority).
  const poolCycleId = activeList
    ? activeList.cycleId ?? tracks.find((t) => t.id === activeList.trackId)?.cycleId ?? null
    : null;
  const poolCycle = poolCycleId ? cycles.find((c) => c.id === poolCycleId) : undefined;
  const poolCycleOwnerNames = useUserNameMap([poolCycle?.ownerId || poolCycle?.createdBy]);
  const rankImportGate = canImportPoolRanks(
    { userId: clerkUserId || userId, isWorkspaceAdmin },
    {
      cycleId: poolCycleId,
      cycle: poolCycle ? { ownerId: poolCycle.ownerId, createdBy: poolCycle.createdBy } : null,
      ownerName: poolCycleOwnerNames[poolCycle?.ownerId || poolCycle?.createdBy || ''],
    },
    permissions.canRankPriorities,
  );

  const handleConfirmGatedReorder = () => {
    if (!activeList || !pendingReorder || !reorderRationale.trim()) return;
    updateRanking(activeList.id, pendingReorder, { rationale: reorderRationale.trim() });
    const orderedPriorities = pendingReorder
      .map((id) => priorities.find((p) => p.id === id))
      .filter((p): p is Priority => !!p);
    applyAboveTheLine(orderedPriorities);
    setPendingReorder(null);
    setReorderRationale('');
    addNotification({
      type: 'info',
      title: 'Reorder Recorded',
      message: 'Published order changed — the decision was memorialized with your rationale',
      duration: 3000
    });
  };
  
  /**
   * Drag the cut line handle up or down the list (owner, 2026-10-15: "drag it
   * up and down, not click it one thing at a time"). The line lands just above
   * the first row whose middle is below the pointer; the counts on the handle
   * follow along, and ONE governed write happens on release.
   */
  const startCutDrag = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!activeList || !permissions.canMoveCutLine || sessionBehindPool || poolFiltered) return;
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    e.preventDefault();
    e.stopPropagation();
    const list = poolListRef.current;
    if (!list) return;
    // The table's rows (ListTable marks each one); the cut line between them is not a row.
    const rowItems = () => Array.from(list.querySelectorAll<HTMLElement>('[data-list-row]'));
    const slotAt = (y: number) => slotForPointer(y, rowItems().map((el) => el.getBoundingClientRect()));
    // The slot the following line is drawn at is the one that is committed on
    // release — what the person sees is what they get, even if the page moves
    // under the pointer between the last move and the release.
    let shownSlot = activeList.cutLinePosition ?? 0;
    const show = (y: number) => {
      shownSlot = slotAt(y);
      setCutDraft(shownSlot);
      setCutDragTop(indicatorTopForSlot(shownSlot, rowItems()));
    };
    const saved = activeList.cutLinePosition ?? 0;
    const onMove = (ev: PointerEvent) => show(ev.clientY);
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      setCutDraft(null);
      setCutDragTop(null);
      if (shownSlot !== saved) void handleCutLineChange(shownSlot);
    };
    const onCancel = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      setCutDraft(null);
      setCutDragTop(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    show(e.clientY);
  };

  // Governed (set_item_pool_cut_line, mig 760). Nothing moves on screen until
  // the server accepts; a published pool first asks for a rationale.
  const handleCutLineChange = async (position: number, rationale?: string, rationaleParts?: RationaleParts | null) => {
    if (!activeList || !permissions.canMoveCutLine || sessionBehindPool || poolFiltered) return;

    const result = await updateCutLine(activeList.id, position, { rationale, rationaleParts });
    if (result.requiresRationale) {
      setPendingCutLine(position);
      return;
    }
    if (!result.success) {
      addNotification({
        type: 'error',
        title: 'Cut line not moved',
        message: result.error ?? 'The cut line could not be moved.',
        duration: 5000,
      });
      return;
    }

    // Update aboveTheLine status for all priorities
    priorities.forEach((priority, index) => {
      const newAboveTheLine = index < position;
      if (priority.aboveTheLine !== newAboveTheLine) {
        const updatedPriority: Priority = {
          ...priority,
          aboveTheLine: newAboveTheLine
        };
        updateItem(priority.id, updatedPriority);
      }
    });

    addNotification({
      type: 'info',
      title: rationale ? 'Cut line moved — decision recorded' : 'Cut line moved',
      message: `${position} above the line, ${Math.max(priorities.length - position, 0)} below. Recorded as a decision.`,
      duration: 3000
    });
  };

  const handleConfirmGatedCutLine = async () => {
    if (pendingCutLine === null || !reorderRationale.trim()) return;
    const position = pendingCutLine;
    const rationale = reorderRationale.trim();
    // The required reason is the reasoning; the other parts ride with it.
    const parts = toRationaleParts({ ...cutLineRationale, rationaleReasoning: '' });
    setPendingCutLine(null);
    setReorderRationale('');
    setCutLineRationale(EMPTY_RATIONALE);
    await handleCutLineChange(position, rationale, parts);
  };
  
  const handleAddItem = (itemId: string) => {
    if (!activeList) return;
    
    // Add item to list (priority = list membership + rank); update candidate context for list
    const newRank = priorities.length + 1;
    const addResult = addPriorityToList(activeList.id, itemId);
    if (!addResult.success) {
      addNotification({
        type: 'error',
        title: 'Item not added',
        message: addResult.blockingIssues?.map((issue) => issue.displayText).join(', ') || addResult.reason || 'This item is missing required prioritization inputs.',
        duration: 5000,
      });
      return;
    }
    promoteToPriority(itemId, activeList.id, newRank);
    
    const item = getItemById(itemId);
    addNotification({
      type: 'success',
      title: 'Item Added',
      message: `"${item?.title}" added to ${UI_LABELS.itemPool.toLowerCase()} at rank ${newRank}`,
      duration: 3000
    });
    
    setShowAddItems(false);
  };

  // Bulk add — used by both the filter/query-builder modal and the add-from-collection
  // modal. Loops the same addPriorityToList → promoteToPriority path handleAddItem uses
  // per item (no new write path), aggregates results, and surfaces any failures instead
  // of silently swallowing them.
  const handleAddItemsBulk = (
    itemIds: string[],
    selectionReason: string | undefined,
    itemSource: 'manual' | 'filter' | 'auto' | 'imported'
  ) => {
    if (!activeList) return;

    let addedCount = 0;
    let nextRank = priorities.length + 1;
    const failures: { title: string; reason: string }[] = [];

    for (const itemId of itemIds) {
      const addResult = addPriorityToList(activeList.id, itemId, activeList.cycleId, effectiveUserId || undefined, selectionReason, itemSource);
      if (!addResult.success) {
        const item = getItemById(itemId);
        failures.push({
          title: item?.title ?? itemId,
          reason: addResult.blockingIssues?.map((issue) => issue.displayText).join(', ') || addResult.reason || 'Missing required prioritization inputs.',
        });
        continue;
      }
      promoteToPriority(itemId, activeList.id, nextRank);
      nextRank += 1;
      addedCount += 1;
    }

    if (addedCount > 0) {
      addNotification({
        type: 'success',
        title: 'Items Added',
        message: `${addedCount} item${addedCount !== 1 ? 's' : ''} added to ${UI_LABELS.itemPool.toLowerCase()}${failures.length > 0 ? ` (${failures.length} skipped)` : ''}.`,
        duration: 4000,
      });
    }
    if (failures.length > 0) {
      const preview = failures.slice(0, 3).map((f) => `"${f.title}": ${f.reason}`).join('; ');
      addNotification({
        type: 'error',
        title: addedCount > 0 ? 'Some items were not added' : 'Items not added',
        message: preview + (failures.length > 3 ? ` +${failures.length - 3} more` : ''),
        duration: 6000,
      });
    }

    setShowAddItems(false);
  };

  const handleRemovePriority = (priorityId: string) => {
    if (!activeList) return;
    
    const priority = getItemById(priorityId) as Priority;
    if (window.confirm(`Remove "${priority?.title}" from this ${UI_LABELS.itemPool.toLowerCase()}?`)) {
      removePriorityFromList(activeList.id, priorityId);
      addNotification({
        type: 'success',
        title: 'Item Removed',
        message: `"${priority?.title}" has been removed from the list`,
        duration: 3000
      });
    }
  };
  
  // After a retire, select another list if one is left.
  const handleListRetired = (retiredId: string) => {
    const remainingLists = itemPools.filter(l => l.id !== retiredId);
    if (remainingLists.length > 0) {
      setActiveItemPool(remainingLists[0].id);
    }
  };
  
  // Check publish permissions (use clerkUserId so button state matches what we need for publish)
  const effectiveUserId = clerkUserId || userId || '';
  const authorityCheck = activeList
    ? canUserPublish(permissions.currentRole, permissions.currentTier || 0, activeList.ownerId, effectiveUserId, {
        listId: activeList.id,
        workspaceId: activeList.workspaceId ?? null,
        isPublished: activeList.isPublished,
      })
    : { allowed: false, reason: 'No list selected' };
  // Who may publish, THEN whether the pool is ready: scoped to a cycle/track and locked (Doc 03 §2.11A).
  const readinessCheck = activeList
    ? checkPoolPublishReadiness(activeList)
    : { allowed: false, reason: 'No list selected' };
  const publishCheck = !clerkUserId
    ? { allowed: false, reason: 'Sign in to publish.' }
    : !authorityCheck.allowed
      ? authorityCheck
      : readinessCheck;
  const canPublish = publishCheck.allowed;

  const handlePublish = useCallback(async () => {
    if (!activeList) {
      addNotification({ type: 'error', title: 'Cannot Publish', message: 'No list selected.', duration: 4000 });
      return;
    }
    if (!clerkUserId) {
      addNotification({ type: 'error', title: 'Cannot Publish', message: 'Please sign in to publish.', duration: 4000 });
      return;
    }
    if (!publishCheck.allowed) {
      addNotification({
        type: 'error',
        title: 'Cannot Publish',
        message: publishCheck.reason,
        duration: 4000
      });
      return;
    }

    setIsPublishing(true);
    try {
      const result = await publishItemPool(
        activeList.id,
        activeList.name,
        (activeList.status || 'draft') as PublishStatus,
        clerkUserId,
        undefined,
        {
          priorityIds: activeList.priorityIds,
          version: (activeList.version || 0) + 1,
          cutLinePosition: activeList.cutLinePosition ?? null,
        },
        toRationaleParts(publishRationale)
      );
      if (result.success && !result.alreadyPublished) setPublishRationale(EMPTY_RATIONALE);
      if (result.success && result.alreadyPublished) {
        // The server recorded nothing new: keep the version and the change log as they are.
        publishItemPoolStore(activeList.id, clerkUserId, undefined, { alreadyPublished: true });
        addNotification({
          type: 'warning',
          title: 'Published version unchanged',
          message: result.message,
          duration: 8000
        });
      } else if (result.success) {
        const criteriaWeights: Record<string, number> = {};
        selectedCriteriaIds.forEach((id) => { criteriaWeights[id] = 1; });
        publishItemPoolStore(activeList.id, clerkUserId, criteriaWeights);
        const updated = useItemPoolsStore.getState().getItemPoolById(activeList.id);
        if (updated?.isPublished) {
          addNotification({
            type: 'success',
            title: 'Published!',
            message: result.message,
            duration: 4000
          });
        } else {
          addNotification({
            type: 'error',
            title: 'Publish incomplete',
            message: 'Publish was recorded but the list could not be updated. You may not have permission to publish this list.',
            duration: 5000
          });
        }
      } else {
        // Not published on the server → not published here either (no local publish).
        addNotification({
          type: 'error',
          title: 'Not published',
          message: result.message,
          duration: 8000
        });
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to publish';
      addNotification({ type: 'error', title: 'Error', message: String(message), duration: 4000 });
    } finally {
      setIsPublishing(false);
    }
  }, [
    activeList,
    clerkUserId,
    publishCheck.allowed,
    publishCheck.reason,
    addNotification,
    publishItemPoolStore,
    selectedCriteriaIds,
    publishRationale,
  ]);
  
  const handleArchive = async () => {
    if (!activeList || !clerkUserId) return;
    
    if (window.confirm(`Archive "${activeList.name}"? This will mark it as no longer active.`)) {
      setIsPublishing(true);
      
      try {
        const result = await archiveItemPool(
          activeList.id,
          activeList.name,
          (activeList.status || 'draft') as PublishStatus,
          clerkUserId
        );
        
        if (result.success) {
          updateItemPool(activeList.id, { status: 'archived' });
          
          addNotification({
            type: 'success',
            title: 'Archived',
            message: result.message,
            duration: 4000
          });
        }
      } catch (error: unknown) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: error instanceof Error ? error.message : 'Failed to archive',
          duration: 4000
        });
      } finally {
        setIsPublishing(false);
      }
    }
  };
  
  // Approve all items above the cut line for the Priority List
  // Authority transition per Build Contract §2.3a — calls approve_priority_list RPC
  const handleApproveForPriorityList = useCallback(async () => {
    if (!activeList || !clerkUserId) return;
    if (!approveRight.allowed) return;

    const aboveItems = priorities.slice(0, activeList.cutLinePosition || priorities.length);
    if (aboveItems.length === 0) {
      addNotification({
        type: 'info',
        title: 'Nothing to ratify',
        message: 'Move the cut line to designate items above it, then ratify.',
        duration: 4000,
      });
      return;
    }

    const alreadyApproved = aboveItems.filter(p => (p as { leadershipApproved?: boolean }).leadershipApproved).length;
    const toApprove = aboveItems.filter(p => !(p as { leadershipApproved?: boolean }).leadershipApproved);

    if (toApprove.length === 0) {
      addNotification({
        type: 'info',
        title: 'Already ratified',
        message: `All ${alreadyApproved} item(s) above the cut line are already on the Priority List.`,
        duration: 4000,
      });
      return;
    }

    const confirm = window.confirm(
      `Ratify ${toApprove.length} item${toApprove.length !== 1 ? 's' : ''} above the cut line for the Priority List?\n\n` +
      `This is an authority action and will be recorded in the governance audit trail.`
    );
    if (!confirm) return;

    setIsApproving(true);
    try {
      await approvePriorityList({
        actorId: clerkUserId,
        itemIds: toApprove.map(p => p.id),
        cycleId: activeList.cycleId ?? null,
        note: `Approved from Item Pool: ${activeList.name}`,
      });

      addNotification({
        type: 'success',
        title: 'Ratified for Priority List',
        message: `${toApprove.length} item${toApprove.length !== 1 ? 's' : ''} ratified. ${alreadyApproved > 0 ? `${alreadyApproved} already ratified.` : ''} View them on the Priorities page.`,
        duration: 5000,
      });
    } catch (err: unknown) {
      addNotification({
        type: 'error',
        title: 'Ratification failed',
        message: err instanceof Error ? err.message : 'Failed to ratify items for Priority List.',
        duration: 5000,
      });
    } finally {
      setIsApproving(false);
    }
  }, [activeList, clerkUserId, approveRight.allowed, priorities, addNotification]);

  // Handle handing off to planning
  const handleHandOffToPlanning = () => {
    if (!activeList || !clerkUserId) return;
    
    const planningRef = window.prompt('Enter a link to the planning system (optional):');
    
    handOffToPlanning(activeList.id, clerkUserId, planningRef || undefined);
    
    addNotification({
      type: 'success',
      title: 'Handed Off',
      message: `"${activeList.name}" has been marked as handed off to planning`,
      duration: 4000
    });
  };
  
  // Handle acknowledging a post-publish change
  const handleAcknowledgeChange = (changeId: string) => {
    if (!activeList || !clerkUserId) return;
    
    acknowledgePostPublishChange(activeList.id, changeId, clerkUserId);
    
    addNotification({
      type: 'info',
      title: 'Change Acknowledged',
      message: 'The change has been marked as reviewed',
      duration: 2000
    });
  };
  
  // Handle acknowledging all post-publish changes
  const handleAcknowledgeAllChanges = () => {
    if (!activeList || !clerkUserId) return;
    
    const unacknowledged = (activeList.postPublishChanges || []).filter(c => !c.acknowledgedAt);
    unacknowledged.forEach(change => {
      acknowledgePostPublishChange(activeList.id, change.id, clerkUserId);
    });
    
    addNotification({
      type: 'info',
      title: 'All Changes Acknowledged',
      message: `${unacknowledged.length} change(s) marked as reviewed`,
      duration: 2000
    });
  };
  
  const handleExportCSV = () => {
    if (!activeList || priorities.length === 0) {
      addNotification({
        type: 'error',
        title: 'Cannot Export',
        message: 'No priorities to export',
        duration: 3000
      });
      return;
    }
    
    // Prepare data for CSV export
    const exportData = priorities.map((priority, index) => {
      // Safely access optional fields
      const priorityRecord = priority as Record<string, unknown>;
      return {
        rank: index + 1,
        title: priority.title,
        description: priority.description || '',
        above_the_line: index < (activeList.cutLinePosition || 0) ? 'Yes' : 'No',
        status: priority.lifecycle_state,
        category: (priorityRecord.category as string | undefined) || '',
        score: (priorityRecord.scores as { composite?: number } | undefined)?.composite ?? priorityRecord.score ?? '',
        created_at: priority.createdAt ? new Date(priority.createdAt).toISOString().split('T')[0] : '',
        id: priority.id,
      };
    });
    
    // Generate CSV content
    const csvContent = generateCSV(exportData, {
      fields: ['rank', 'title', 'description', 'above_the_line', 'status', 'category', 'score', 'created_at', 'id'],
      includeHeaders: true,
    });
    
    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const filename = `${activeList.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.csv`;
    
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    
    addNotification({
      type: 'success',
      title: 'CSV Exported',
      message: `Exported ${priorities.length} priorities to ${filename}`,
      duration: 3000
    });
  };
  
  // Handle items imported from integrations (Miro/Jira)
  // Items are added to the global items store, not tied to a specific Item Pool
  const handleIntegrationImport = async (
    items: Array<{
      title: string;
      description?: string;
      source: string;
      externalId: string;
      itemCategory?: string;
      altitudeLevel?: number | string;
      metaCategory?: string;
      strategicBucket?: string;
      tags?: string[];
      [key: string]: unknown;
    }>,
  ): Promise<ImportSaveOutcome> => {
    // Create each imported item (lifecycle_stage: intake), awaiting each save
    // and counting only confirmed ones. The panel toasts from this outcome.
    let skipped = 0;
    const { created, failures, savedIds } = await saveImportRows(items, (item) => {
      // Rows missing a title or source id cannot be keyed; not created.
      if (!item.title || !item.externalId) return Promise.resolve(null);
      // items.id is a uuid, so the source key ("miro-<sticky id>") cannot be the
      // id itself — every save was refused. A name-based uuid is the same every
      // time for the same sticky, which keeps the skip-if-already-here check.
      const sourceItemId = uuidv5(`${item.source}-${item.externalId}`, uuidv5.URL);
      // Already in the system from an earlier import: skip, don't duplicate.
      if (getItemById(sourceItemId)) {
        skipped++;
        return Promise.resolve(null);
      }
      const altitude =
        item.altitudeLevel !== undefined && item.altitudeLevel !== null
          ? String(item.altitudeLevel)
          : undefined;
      return createItemWithReason({
        id: sourceItemId,
        title: item.title,
        description: item.description,
        source: item.source,
        createdBy: userId || 'integration',
        tags: Array.isArray(item.tags) && item.tags.length > 0
          ? item.tags
          : [item.source, 'imported'],
        ...(typeof item.itemCategory === 'string' ? { itemCategory: item.itemCategory as never } : {}),
        ...(altitude ? { altitudeLevel: altitude as never } : {}),
        ...(typeof item.metaCategory === 'string' ? { metaCategory: item.metaCategory as never } : {}),
        ...(typeof item.strategicBucket === 'string'
          ? { strategicBucket: item.strategicBucket as never }
          : {}),
      });
    }, (item) => item.title || 'Untitled');
    return { created, failures, skipped, savedIds };
  };

  // Status badge component
  const StatusBadge = ({ status }: { status?: string }) => {
    const statusConfig: Record<string, { icon: React.ReactNode; className: string; label: string }> = {
      draft: { icon: <Clock className="h-3 w-3" />, className: 'bg-[var(--hover-tint)] text-[var(--text-sec)]', label: 'Draft' },
      pending_approval: { icon: <Clock className="h-3 w-3" />, className: 'bg-[var(--mustard-dim)] text-[var(--mustard)]', label: 'Pending Approval' },
      published: { icon: <CheckCircle className="h-3 w-3" />, className: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]', label: 'Published' },
      archived: { icon: <Archive className="h-3 w-3" />, className: 'bg-[var(--hover-tint)] text-[var(--text-sec)]', label: 'Archived' },
    };
    
    const config = statusConfig[status || 'draft'] || statusConfig.draft;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };
  
  const handleFindDuplicates = async () => {
    if (!activeList || priorities.length < 2) {
      addNotification({
        type: 'info',
        title: 'Not enough items',
        message: 'Add at least 2 items to this pool before scanning for duplicates.',
        duration: 4000,
      });
      return;
    }
    setIsClusterLoading(true);
    setClusterResult(null);
    try {
      const items = priorities.map((p) => ({
        id: p.id,
        title: p.title,
        description: (p as { description?: string }).description,
      }));
      const result = await suggestClusters(items);
      setClusterResult(result);
      if (result.suggestedGroups.length === 0) {
        addNotification({
          type: 'success',
          title: 'No duplicates found',
          message: 'The AI found no likely duplicates or related clusters in this pool.',
          duration: 4000,
        });
      }
    } catch {
      addNotification({
        type: 'error',
        title: 'AI scan failed',
        message: 'Could not connect to the LLM service. Check your API key configuration.',
        duration: 5000,
      });
    } finally {
      setIsClusterLoading(false);
    }
  };

  const aboveTheLine = priorities.slice(0, activeList?.cutLinePosition || 0);
  const belowTheLine = priorities.slice(activeList?.cutLinePosition || 0);
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={<ListChecks className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title={`${UI_LABELS.itemPool} Editor`}
        subtitle="Order the items and manage the cut line"
        actions={
          canCreatePool ? (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] transition-colors"
            >
              <Plus className="h-4 w-4" />
              New Pool
            </button>
          ) : undefined
        }
      />

      {/* List–detail layout: pool list (left) + editor content (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Left: Pool list */}
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden lg:max-h-[calc(100vh-12rem)] flex flex-col">
          <div className="px-4 py-3 border-b border-[var(--rule)]">
            <h2 className="text-sm font-semibold text-[var(--text-pri)]">
              {UI_LABELS.itemPools || 'Item Pools'}
            </h2>
          </div>
          {itemPools.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center py-8 text-center text-[var(--text-ter)] px-4">
              <ListChecks className="h-8 w-8 text-[var(--text-ter)] mb-2" />
              <p className="text-sm font-medium text-[var(--text-sec)]">No pools yet</p>
              <p className="text-xs mt-1 text-[var(--text-ter)] max-w-[200px]">
                A pool is the set of items you choose to rank, picked before any session runs.
              </p>
              {canCreatePool && (
                <button
                  type="button"
                  onClick={() => setCreateOpen(true)}
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] transition-colors"
                >
                  <Plus className="h-3.5 w-3.5" />
                  New Pool
                </button>
              )}
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-[var(--rule)]">
              {itemPools.map((list) => {
                const isActive = list.id === activeItemPoolId;
                return (
                  <button
                    key={list.id}
                    type="button"
                    onClick={() => setActiveItemPool(list.id)}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      isActive
                        ? 'bg-primary-50 text-primary-900'
                        : 'hover:bg-[var(--surface)] text-[var(--text-pri)]'
                    }`}
                  >
                    <div className={`font-medium text-sm truncate ${isActive ? 'text-primary-800' : 'text-[var(--text-pri)]'}`}>{list.name}</div>
                    <div className="text-xs text-[var(--text-ter)] mt-0.5">
                      {list.priorityIds.length} {list.priorityIds.length === 1 ? 'item' : 'items'}
                      {list.status && list.status !== 'draft' && ` · ${list.status}`}
                      {(list.handedOffToPlanningAt || (list.linkedPlanIds ?? []).length > 0) && ' · in a plan'}
                    </div>
                    {(list.cycleId || list.trackId) && (
                      <div className="text-xs text-[var(--text-ter)] mt-0.5 truncate">
                        {[list.cycleId ? cycleNameById.get(list.cycleId) : null, list.trackId ? trackNameById.get(list.trackId) : null].filter(Boolean).join(' · ')}
                      </div>
                    )}
                    {(list.tags ?? []).length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {(list.tags ?? []).slice(0, 2).map((tag) => (
                          <span
                            key={`${list.id}-${tag}`}
                            className={CLASSIFICATION_CHIP_CLASS}
                          >
                            {tag}
                          </span>
                        ))}
                        {(list.tags ?? []).length > 2 && (
                          <span className={CHIP_OVERFLOW_CLASS} title={(list.tags ?? []).join(', ')}>
                            +{(list.tags ?? []).length - 2} more
                          </span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {retiredItemPools.length > 0 && (
            <details className="border-t border-[var(--rule)] px-4 py-2">
              <summary className="cursor-pointer text-xs text-[var(--text-ter)]">
                Retired ({retiredItemPools.length})
              </summary>
              <ul className="mt-2 space-y-2">
                {retiredItemPools.map((pool) => (
                  <li key={pool.id} className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm text-[var(--text-sec)]">{pool.name}</span>
                    <ReverseActionButton
                      label="Take back"
                      confirmTitle={`Take back “${pool.name}”`}
                      explain="It returns to your lists in the status it had, with its items."
                      run={(reason) => restoreRetiredItemPool(pool.id, reason)}
                    />
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        {/* Right: Editor content for selected pool */}
        <div className="min-w-0 space-y-6">
          {/* Toolbar: pool name, status, actions (when a pool is selected) */}
          {activeList && (
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-4 py-3 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-[var(--text-pri)] truncate">{activeList.name}</h3>
                  <StatusBadge status={activeList.status} />
                </div>
                <p className="text-xs text-[var(--text-ter)] mt-0.5">
                  {activeList.description && `${activeList.description.slice(0, 60)}${activeList.description.length > 60 ? '…' : ''}`}
                </p>
                <StructureVersionFinePrint table="item_pools" id={activeList.id} mode={activeList.poolLockedAt ? 'locked' : 'started'} className="mt-0.5" />
                {(activeList.tags ?? []).length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {(activeList.tags ?? []).slice(0, 4).map((tag) => (
                      <span key={`${activeList.id}-${tag}`} className={CLASSIFICATION_CHIP_CLASS}>{tag}</span>
                    ))}
                    {(activeList.tags ?? []).length > 4 && (
                      <span className={CHIP_OVERFLOW_CLASS} title={(activeList.tags ?? []).join(', ')}>
                        +{(activeList.tags ?? []).length - 4} more
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* 1017: drawn only where the database will take it. A person the cycle's
                    decision right shuts out reads the database's own sentence instead (1001). */}
                {activeList.isPublished && (activeList.cutLinePosition ?? 0) > 0 && (
                  refusedByRight(approveRight) ? (
                    <p className="max-w-xs text-xs text-[var(--text-ter)]">{approveRight.line}</p>
                  ) : approveRight.allowed && (
                    <button
                      onClick={handleApproveForPriorityList}
                      disabled={isApproving}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 transition-colors"
                      title="Ratify the items above the cut line for the Priority List (governance action)"
                    >
                      <ShieldCheck className="h-4 w-4" />
                      {isApproving ? 'Ratifying…' : 'Ratify for Priority List'}
                    </button>
                  )
                )}
                {activeList.status === 'published' && (
                  <button
                    onClick={handleArchive}
                    disabled={isPublishing}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] disabled:opacity-50 transition-colors"
                    title={`Archive this ${UI_LABELS.itemPool.toLowerCase()}`}
                  >
                    <Archive className="h-4 w-4" />
                    Archive
                  </button>
                )}
                {activeList.priorityIds.length > 0 && (
                  <button
                    onClick={handleExportCSV}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors"
                    title="Export list to CSV"
                  >
                    <Download className="h-4 w-4" />
                    CSV
                  </button>
                )}
                <ReverseActionButton
                  label="Retire"
                  confirmTitle={`Retire ${UI_LABELS.itemPool.toLowerCase()} “${activeList.name}”`}
                  explain={`The ${UI_LABELS.itemPool.toLowerCase()} leaves every list. Its items and history stay, and you can take it back. A ${UI_LABELS.itemPool.toLowerCase()} that is locked, published or already used by a session can't be retired.`}
                  run={(reason) => retireItemPool(activeList.id, reason)}
                  onDone={() => handleListRetired(activeList.id)}
                  title={`Retire this ${UI_LABELS.itemPool.toLowerCase()}`}
                />
              </div>
            </div>
          )}
      
      {/* Publication Status & Post-Publish Changes */}
      {activeList && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Publication Status - use same async handlePublish so errors surface */}
          <div className="space-y-3">
            <PublicationStatusBadge
              focusList={activeList}
              canPublish={canPublish}
              publishDisabledReason={publishCheck.reason}
              isPublishing={isPublishing}
              onPublish={handlePublish}
              onHandOff={handleHandOffToPlanning}
            />
            {/* Pool creation's moment (985): why this list exists, recorded on
                the Decision its publication writes. */}
            {!activeList.isPublished && canPublish && (
              <RationaleFields
                value={publishRationale}
                onChange={setPublishRationale}
                title="Why this list — saved when you publish it"
                disabled={isPublishing}
              />
            )}
          </div>

          {/* Publication is memorialized (mig 584) — there is no unpublish. A
              changed list is published again as a new version that supersedes
              the previous one (mig 746, canon 27 §6). */}
          {activeList.isPublished && (activeList.ownerId === effectiveUserId || isWorkspaceAdmin) && (
            <div className="flex items-start gap-3 border border-[var(--rule)] bg-[var(--bg-raised)] p-3 text-xs text-[var(--text-sec)]">
              <p className="flex-1">
                Published lists can't be unpublished. If the list has changed, publish it again as a new version — the
                earlier publication stays in the record, marked as replaced.
              </p>
              <ReverseActionButton
                label="Publish new version"
                confirmTitle="Publish a new version of this list"
                explain="This records the list as it stands now as a new publication. The previous publication is kept and marked as replaced. Say why the list changed."
                run={(reason) => republishItemPool(activeList.id, reason)}
                onDone={() => void fetchItemPools()}
              />
            </div>
          )}
          
          {/* Post-Publish Change Alert */}
          {activeList.isPublished && (activeList.postPublishChanges?.length ?? 0) > 0 && (
            <PostPublishChangeAlert
              focusList={activeList}
              onAcknowledge={handleAcknowledgeChange}
              onAcknowledgeAll={handleAcknowledgeAllChanges}
            />
          )}
        </div>
      )}
      
      {/* Unscoped draft — Doc 03 §2.11A: scope is required at lock. Assign here. */}
      {activeList && !activeList.cycleId && !activeList.trackId && (
        <PoolScopeAssignBanner pool={activeList} canEdit={permissions.canRankPriorities} />
      )}

      {/* Query feed — diffed refresh while open, Arrival Bay once severed at lock (PRD §4.3) */}
      {activeList && queryFedPoolsEnabled && (
        <PoolFeedPanel
          pool={activeList}
          candidateItems={allItems}
          actorId={userId ?? undefined}
          canEdit={permissions.canRankPriorities}
        />
      )}

      {/* Collapsible Integration Sync Panel */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
        <button
          onClick={() => setShowIntegrations(!showIntegrations)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-[var(--surface)] transition-colors"
        >
          <div className="flex items-center gap-2 text-sm font-medium text-[var(--text-sec)]">
            <Plug className="h-4 w-4 text-[var(--text-ter)]" />
            Integrations (Miro, Jira, Excel/CSV)
          </div>
          {showIntegrations ? (
            <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" />
          ) : (
            <ChevronRight className="h-4 w-4 text-[var(--text-ter)]" />
          )}
        </button>
        {showIntegrations && (
          <div className="p-4 border-t border-[var(--rule)]">
            <IntegrationSyncPanel
              listId={activeList?.id || 'no-list'}
              listName={activeList?.name || 'No List Selected'}
              priorities={activeList ? priorities.map((p, idx) => ({
                ...p,
                rank: idx + 1,
                aboveTheLine: idx < (activeList.cutLinePosition ?? 0),
              })) : []}
              onImportItems={handleIntegrationImport}
              onNotification={(notification) => addNotification({ ...notification, duration: notification.duration ?? 4000 })}
              rankImport={activeList ? {
                gate: rankImportGate,
                apply: (orderedIds) => applyOrder(orderedIds, 'The pool now follows the ranks in the imported file'),
              } : undefined}
            />
          </div>
        )}
      </div>

      {!activeList ? (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-12 text-center">
          <ListChecks className="h-8 w-8 mx-auto mb-2 text-[var(--text-ter)]" />
          <p className="text-sm font-medium text-[var(--text-sec)]">No {UI_LABELS.itemPool.toLowerCase()} selected</p>
          <p className="text-xs text-[var(--text-ter)] mt-1">
            {/* "From the list": the list is on the left on a wide screen and above on a phone. */}
            {canCreatePool ? 'Select a pool from the list, or create one.' : 'Select a pool from the list.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Stats strip */}
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
            <div className="grid grid-cols-4 divide-x divide-[var(--rule)]">
              {[
                { label: 'Total', value: priorities.length, color: 'text-[var(--text-pri)]' },
                { label: 'Above line', value: aboveTheLine.length, color: 'text-[var(--green)] dark:text-[var(--text-ter)]' },
                { label: 'Below line', value: belowTheLine.length, color: 'text-[var(--text-ter)]' },
                { label: 'Available to add', value: availableItems.length, color: 'text-[var(--mustard)]' },
              ].map(({ label, value, color }) => (
                <div key={label} className="px-4 py-3">
                  <p className="text-xs text-[var(--text-ter)]">{label}</p>
                  <p className={`text-base font-semibold tabular-nums ${color}`}>{value}</p>
                </div>
              ))}
            </div>
          </div>
          
          {/* AI Clustering Results Panel */}
          {clusterResult && clusterResult.suggestedGroups.length > 0 && (
            <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-[var(--text-sec)] text-sm">
                      AI found {clusterResult.suggestedGroups.length} potential {clusterResult.suggestedGroups.length === 1 ? 'cluster' : 'clusters'}
                    </h3>
                    {clusterResult.reasoning && (
                      <p className="text-xs text-[var(--text-sec)] mt-0.5">{clusterResult.reasoning}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setClusterResult(null)}
                  className="p-1 hover:bg-[var(--slate-dim)] rounded transition-colors"
                  title="Dismiss"
                >
                  <X className="h-4 w-4 text-[var(--text-ter)]" />
                </button>
              </div>
              <div className="space-y-3">
                {clusterResult.suggestedGroups.map((group, idx) => {
                  const relLabel: Record<string, string> = {
                    duplicate: 'Likely Duplicates',
                    variant: 'Variants of the Same Need',
                    related: 'Related Items',
                    decomposition: 'Decomposition',
                    composition: 'Composition',
                  };
                  const confidenceColor: Record<string, string> = {
                    high: 'bg-[var(--coral-dim)] text-[var(--coral)]',
                    medium: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
                    low: 'bg-[var(--hover-tint)] text-[var(--text-sec)]',
                  };
                  return (
                    <div key={idx} className="bg-[var(--bg-raised)] border border-[var(--slate)] p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <GitMerge className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                        <span className="text-xs font-semibold text-[var(--text-ter)]">
                          {relLabel[group.relationType] ?? group.relationType}
                        </span>
                        <span className={`ml-auto text-xs px-1.5 py-0.5 rounded-full font-medium ${confidenceColor[group.confidence] ?? confidenceColor.medium}`}>
                          {group.confidence} confidence
                        </span>
                      </div>
                      <ul className="space-y-1 mb-2">
                        {group.itemIds.map((id) => {
                          const item = getItemById(id);
                          return (
                            <li key={id} className="flex items-center gap-1.5">
                              {id === group.suggestedCanonicalId && (
                                <span className="text-xs bg-[var(--slate-dim)] text-[var(--text-sec)] px-1 rounded font-medium">canonical</span>
                              )}
                              <span className="text-sm text-[var(--text-pri)]">{item?.title ?? id}</span>
                            </li>
                          );
                        })}
                      </ul>
                      <div className="flex items-start gap-1.5">
                        <AlertTriangle className="h-3 w-3 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-[var(--text-sec)]">{group.rationale}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-[var(--text-sec)] mt-3 text-center">
                AI suggestions are advisory only — review and merge items manually.
              </p>
            </div>
          )}

          {/* Add Items Panel */}
          {showAddItems && (
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
              <div className="px-4 py-3 border-b border-[var(--rule)] flex items-center justify-between">
                <h3 className="text-sm font-semibold text-[var(--text-pri)]">Add Items to Pool</h3>
                <div className="flex items-center gap-3">
                  {activeList && (
                    <Link
                      to={poolQueryLink(activeList.id, location.pathname + location.search)}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700"
                    >
                      <ListFilter className="h-3.5 w-3.5" />
                      Find items with a query…
                    </Link>
                  )}
                  <button
                    onClick={() => setShowCollectionModal(true)}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700"
                  >
                    <FolderOpen className="h-3.5 w-3.5" />
                    Add from collection…
                  </button>
                  <button
                    onClick={() => setShowAddItems(false)}
                    className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {/* zero/one/few/many: at `many` the list is unusable without a
                  search box (F14 — the demo workspace had 60+ items). */}
              {availableCountState === 'many' && (
                <div className="px-4 py-2.5 border-b border-[var(--rule)] flex items-center gap-2">
                  <Search className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                  <input
                    type="search"
                    value={addItemsQuery}
                    onChange={(e) => setAddItemsQuery(e.target.value)}
                    placeholder={`Search ${availableItems.length} available items…`}
                    aria-label="Search available items"
                    data-testid="add-items-search"
                    className="flex-1 text-sm bg-transparent outline-none placeholder:text-[var(--text-ter)]"
                  />
                  <span className="text-[11px] font-medium text-[var(--text-ter)] flex-shrink-0">
                    {filteredAvailableItems.length} of {availableItems.length}
                  </span>
                </div>
              )}

              {/* In this pool / Available split with counts, per the inline-list
                  pattern in the zero/one/few/many rule. */}
              {availableCountState === 'many' && (
                <div className="px-4 py-1.5 bg-[var(--surface)]/70 border-b border-[var(--rule)] flex items-center justify-between text-[11px] font-medium uppercase tracking-wide text-[var(--text-ter)]">
                  <span>Available</span>
                  <span>In this pool · {poolMemberIds.size}</span>
                </div>
              )}

              {/* F13: readiness gates ranking, not membership. Unready items are
                  addable and flagged, so a seeded workspace can fill a pool
                  through the UI instead of through SQL. */}
              {unreadyAvailableCount > 0 && (
                <div className="px-4 py-2.5 bg-[var(--mustard-dim)] border-b border-[var(--mustard)] flex items-start gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-[var(--mustard)]" data-testid="add-items-readiness-banner">
                    {unreadyAvailableCount} of {filteredAvailableItems.length} item
                    {filteredAvailableItems.length !== 1 ? 's' : ''} shown are missing required values —
                    they can be added to the pool, but can’t be scored until filled in.
                  </p>
                </div>
              )}

              {filteredAvailableItems.length > 0 ? (
                <div className="divide-y divide-[var(--rule)]">
                  {filteredAvailableItems.map((item) => (
                    <div key={item.id} className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--surface)]/60">
                      <div className="flex-1 min-w-0">
                        <Link
                          to={itemHref(item.id, 'values')}
                          className="text-sm font-medium text-[var(--text-pri)] truncate block hover:underline hover:text-[var(--text-ter)] transition-colors"
                          title="Open item to fill in missing values"
                        >
                          {item.title}
                        </Link>
                        <div className="mt-1">
                          <ReadinessBadge item={item} requiredAttributes={requiredAttributes} />
                        </div>
                      </div>
                      <button
                        onClick={() => handleAddItem(item.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                      >
                        <Plus className="h-3 w-3" /> Add
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-[var(--text-ter)]">
                  {addItemsQuery.trim() ? (
                    <>
                      <p className="text-sm">No items match “{addItemsQuery.trim()}”.</p>
                      <button
                        onClick={() => setAddItemsQuery('')}
                        className="mt-3 text-xs font-medium text-primary-600 hover:underline"
                      >
                        Clear search
                      </button>
                    </>
                  ) : allItems.length === 0 ? (
                    <>
                      <p className="text-sm">No items yet.</p>
                      <p className="text-xs mt-1 text-[var(--text-ter)]">Add items via Intake, then they'll appear here.</p>
                      <Link to="/prioritization/items" className="inline-block mt-3 text-xs font-medium text-primary-600 hover:underline">
                        Go to Items →
                      </Link>
                    </>
                  ) : (
                    <>
                      <p className="text-sm">All items are already in this pool.</p>
                      <p className="text-xs mt-1 text-[var(--text-ter)]">
                        Create a new item in Intake to add more.
                      </p>
                      <Link to="/prioritization/items" className="inline-block mt-3 text-xs font-medium text-primary-600 hover:underline">
                        Go to Items →
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* The ranked members — the item list's block, in rank order */}
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
            <div className="px-4 py-3 border-b border-[var(--rule)] flex items-center justify-between">
              <h2 className="text-sm font-semibold text-[var(--text-pri)]">Ranked items</h2>
              <div className="flex items-center gap-2">
                {priorities.length > 0 && (
                  <>
                    <ItemQueryFiltersButton ws={ws} />
                    <ItemQueryColumnsButton ws={ws} />
                  </>
                )}
                {priorities.length >= 2 && poolClusteringEnabled && (
                  <button
                    onClick={handleFindDuplicates}
                    disabled={isClusterLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] disabled:opacity-50 transition-colors"
                    title="AI: Scan this pool for likely duplicates and related items"
                  >
                    <Sparkles className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                    {isClusterLoading ? 'Scanning…' : 'Find Duplicates'}
                  </button>
                )}
                {permissions.canRankPriorities && (
                  <button
                    onClick={() => setShowAddItems(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Items
                  </button>
                )}
              </div>
            </div>
            
            {prioritiesPending ? (
              <div className="text-center py-10 text-[var(--text-ter)]" data-testid="pool-priorities-loading">
                <Loader2 className="h-8 w-8 mx-auto mb-2 text-[var(--text-ter)] animate-spin" />
                <p className="text-sm font-medium text-[var(--text-sec)]">Loading this pool’s items…</p>
                <p className="text-xs text-[var(--text-ter)] mt-1">
                  {activeList.priorityIds.length} item{activeList.priorityIds.length !== 1 ? 's' : ''} in this pool
                </p>
              </div>
            ) : priorities.length === 0 ? (
              <div className="text-center py-10 text-[var(--text-ter)]">
                <ListChecks className="h-8 w-8 mx-auto mb-2 text-[var(--text-ter)]" />
                <p className="text-sm font-medium text-[var(--text-sec)]">No priorities yet</p>
                <p className="text-xs text-[var(--text-ter)] mt-1">Add items from your workspace</p>
              </div>
            ) : (
              <>
                {/* The query here narrows the view of the members; it makes nothing of them. */}
                <ItemQueryBar
                  ws={ws}
                  view={{ sort: ws.sort, groupBy: null, visibleColumns: ws.visibleColumns, activeChart: 'category' }}
                  resultActions={false}
                />
                {poolFiltered && (
                  <div
                    className="flex items-start gap-3 border-b border-[var(--mustard)] bg-[var(--mustard-dim)] px-4 py-2.5 text-xs text-[var(--mustard)]"
                    role="status"
                    data-testid="pool-filtered-notice"
                  >
                    <ListFilter className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                    <p className="flex-1">
                      Filtered: showing {ws.filteredCount} of {priorities.length} — the cut line and order are unchanged.
                      {permissions.canRankPriorities || permissions.canMoveCutLine ? ' Clear the filter to reorder or move the cut line.' : ''}
                      {ws.orderIgnored ? ' The pool keeps its rank order, so ORDER BY is not applied here.' : ''}
                    </p>
                    <button type="button" onClick={ws.clearQuery} className="flex-shrink-0 font-medium underline hover:no-underline">
                      Clear filter
                    </button>
                  </div>
                )}
                <ItemQueryRows ws={ws} className="flex min-h-0">
                  {ws.displayRows.length === 0 ? (
                    <div className="flex-1 py-10 text-center text-[var(--text-ter)]">
                      <p className="text-sm font-medium text-[var(--text-sec)]">No members of this pool match the filter</p>
                      <button type="button" onClick={ws.clearQuery} className="mt-3 text-xs font-medium text-primary-600 hover:underline">
                        Clear filter
                      </button>
                    </div>
                  ) : (
                    <ItemQueryTable
                      ws={ws}
                      selectable={false}
                      onRowClick={() => {}}
                      onTitleClick={(item: ItemRow) => navigate(itemHref(item.id))}
                      ownerNameMap={memberNames}
                      creatorNameMap={memberNames}
                      ranked={{
                        renderRank: (item) => {
                          const place = poolIndexById.get(item.id) ?? 0;
                          // Never official: a pool's order is not a ranking anyone has agreed to.
                          return <RankMark rank={place + 1} official={false} aboveTheLine={place < activeList.cutLinePosition} size="sm" />;
                        },
                        // No handles while a filter is on: the rows shown are not the whole order.
                        onReorder: permissions.canRankPriorities && !poolFiltered ? handleReorder : undefined,
                        // The cut line sits before the first row below it; with a filter on, before the first shown row below it.
                        divider: {
                          index: poolFiltered
                            ? cutLineSlotAmong(ws.displayRows.map((r) => poolIndexById.get(r.id) ?? 0), activeList.cutLinePosition)
                            : cutLineSlot(activeList.cutLinePosition, priorities.length),
                          node: (
                            <CutLineControl
                              position={activeList.cutLinePosition}
                              totalItems={priorities.length}
                              onMove={(p) => void handleCutLineChange(p)}
                              canMove={permissions.canMoveCutLine && !sessionBehindPool && !poolFiltered}
                              setBySession={sessionBehindPool}
                              onDragStart={startCutDrag}
                              draftPosition={cutDraft}
                            />
                          ),
                        },
                        // While the handle is dragged, this line follows the pointer; the saved line stays put until the server accepts.
                        overlay:
                          cutDragTop !== null ? (
                            <div
                              aria-hidden
                              data-testid="cut-line-drag-indicator"
                              className="pointer-events-none absolute left-0 right-0 z-10 border-t-4 border-dashed border-[var(--coral)]"
                              style={{ top: cutDragTop }}
                            />
                          ) : undefined,
                        rowsRef: poolListRef,
                      }}
                      rowActionsWidth={permissions.canRankPriorities ? 72 : 44}
                      renderRowActions={(item) => {
                        const place = (poolIndexById.get(item.id) ?? 0) + 1;
                        return (
                          <>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); openTrail(item.id); }}
                              className="p-1 text-[var(--text-ter)] hover:text-primary-600 rounded transition-colors"
                              title={`Why is this #${place}? Open the decision trail`}
                              aria-label={`Why is this #${place}?`}
                            >
                              <Landmark className="h-3.5 w-3.5" />
                            </button>
                            {permissions.canRankPriorities && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleRemovePriority(item.id); }}
                                className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)] rounded transition-colors"
                                title="Remove from list"
                                aria-label={`Remove ${item.title} from the pool`}
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </>
                        );
                      }}
                    />
                  )}
                </ItemQueryRows>
              </>
            )}
          </div>
        </div>
      )}
        </div>
      </div>

      {/* Add-from-collection — opened from "Add from collection…" above. */}
      {activeList && (
        <AddFromCollectionModal
          open={showCollectionModal}
          onClose={() => setShowCollectionModal(false)}
          poolId={activeList.id}
          excludeItemIds={activeList.priorityIds}
          onAdd={(itemIds, selectionReason) => {
            handleAddItemsBulk(itemIds, selectionReason, 'manual');
            setShowCollectionModal(false);
          }}
        />
      )}

      {/* Gated reorder — a published pool's order only changes with a
          recorded rationale (canon 27: who, when, why). */}
      {(pendingReorder || pendingCutLine !== null) && activeList && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="reorder-gate-title">
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] w-full max-w-md p-6">
            <div className="flex items-start gap-3 mb-3">
              <div className="p-2 bg-[var(--mustard-dim)] flex-shrink-0">
                <ShieldCheck className="h-5 w-5 text-[var(--mustard)]" />
              </div>
              <div>
                <h3 id="reorder-gate-title" className="text-base font-semibold text-[var(--text-pri)]">
                  {pendingCutLine !== null ? 'Moving the cut line on a published list' : 'Reordering a published list'}
                </h3>
                <p className="text-sm text-[var(--text-ter)] mt-1">
                  {pendingCutLine !== null
                    ? `This list is published — moving the cut line to ${pendingCutLine} is a recorded decision.`
                    : 'This list is published — changing its order is a recorded decision.'}
                  {' '}Your rationale will be memorialized with your name and the time.
                </p>
              </div>
            </div>
            <textarea
              value={reorderRationale}
              onChange={(e) => setReorderRationale(e.target.value)}
              placeholder={pendingCutLine !== null ? 'Why is the cut line moving? (required)' : 'Why is this order changing? (required)'}
              rows={3}
              autoFocus
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-[var(--slate)]"
            />
            {pendingCutLine !== null && (
              <RationaleFields
                className="mt-3"
                value={cutLineRationale}
                onChange={setCutLineRationale}
                omitReasoning
                title="More on why"
              />
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => { setPendingReorder(null); setPendingCutLine(null); setReorderRationale(''); setCutLineRationale(EMPTY_RATIONALE); }}
                className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={pendingCutLine !== null ? handleConfirmGatedCutLine : handleConfirmGatedReorder}
                disabled={!reorderRationale.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {pendingCutLine !== null ? 'Record decision & move cut line' : 'Record decision & reorder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* "Why is this #N?" — Decision Trail inspector */}
      <DecisionTrailPanel
        open={trailItemId != null}
        onClose={closeTrail}
        itemId={trailItemId}
        rankLabel={(() => {
          if (!trailItemId || !activeList) return null;
          const idx = activeList.priorityIds.indexOf(trailItemId);
          return idx >= 0 ? idx + 1 : null;
        })()}
        contextLabel={activeList ? `${UI_LABELS.itemPool}: ${activeList.name}` : null}
      />
      <CreateItemPoolModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handlePoolCreated}
      />
    </div>
  );
}

